import React, { useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { Company, companySchema, CURRENCIES, SYSTEM_OF_MEASUREMENTS } from "./Company";
import { makeRequired } from "../../shared/components/forms/formUtils";
import { handleFormError, RhfForm } from "../../shared/components/forms/RhfForm";
import { FormHeader } from "../../shared/components/forms/FormHeader";
import { MuiRhfTextField } from "../../shared/components/forms/MuiRhfTextField";
import { yupResolver } from "@hookform/resolvers/yup";
import { UploadButton } from "../../shared/components/buttons/UploadButton";
import { uploadFile } from "../../shared/components/file-upload/fileApi";
import { useAxios } from "../../shared/technical/rest/AxiosProvider";
import { CompanyImageField } from "./CompanyImageField";
import { ContactPersonForm } from "./ContactPersonForm";
import { MuiRhfSelectField } from "../../shared/components/forms/MuiRhfSelectField";
import { MuiRhfDatePicker } from "../../shared/components/forms/MuiRhfDatePicker";
import { CountriesSelectField } from "../../shared/technical/i18n/CountriesSelectField";

export function CompanyForm(props: {
  company: Company;
  handleSave: (company: Company) => Promise<Company>;
  handleDelete: (company: Company) => Promise<void>;
  handleClose: () => void;
}) {
  const { t } = useTranslation();

  const [ formReadonly, setFormReadonly ] = useState<boolean>(!!props.company.id);
  const axios = useAxios();
  const methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: props.company,
    resolver: yupResolver(companySchema(t)),
  });
  const { reset, register, setValue, setError } = methods;

  useEffect(() => {
    register("id");
  }, [ register ]);

  useEffect(() => {
    reset(props.company);
  }, [ props.company, reset ]);

  const handleSave = useCallback(async (entity: Company) => {
    try {
      return await props.handleSave(entity);
    } catch (error) {
      handleFormError(error, setError, t);
    }
  }, [ props, setError, t ]);

  const stdFieldProps = {
    variant: "outlined" as "outlined",
    disabled: formReadonly,
    InputLabelProps: {
      shrink: true,
    },
  };

  const stdListFieldProps = {
    variant: "outlined" as "outlined",
    disabled: formReadonly,
  }

  const required = makeRequired(companySchema(t));
  const isNew = !props.company.id;
  const editFormTitle = isNew ? t("Company.titleNew") : t("Company.titleEdit") + ": " + props.company.name;
  return (
    <FormProvider {...methods}>
      <RhfForm id={"company-form"} onSubmit={handleSave}>
        <FormHeader
          title={editFormTitle}
          onCancel={props.handleClose}
          onDelete={() => props.handleDelete(props.company)}
          canBeEdited={true}
          readonly={formReadonly}
          onStartEditing={() => setFormReadonly(false)}
        />
        <Grid container={true} sx={{ margin: 2, width: "auto" }}>
          <Grid item={true} xs={12}>
            <Grid container={true} spacing={3}>
              <Grid item={true} xs={12} md={8}>
                <MuiRhfTextField name={"name"} label={t("Company.name")} fullWidth={true} required={required?.name} {...stdFieldProps} />
              </Grid>
              <Grid item={true} xs={12} md={4}>
                <MuiRhfTextField
                  name={"customerNumber"}
                  label={t("Company.customerNumber")}
                  fullWidth={true}
                  required={required?.customerNumber}
                  {...stdFieldProps}
                />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
                <CountriesSelectField name={"countryIsoCode"} required={required?.country} {...stdListFieldProps} />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
                <MuiRhfTextField name={"city"} label={t("Company.city")} fullWidth={true} required={required?.city} {...stdFieldProps} />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
                <MuiRhfTextField
                  name={"street"}
                  label={t("Company.street")}
                  fullWidth={true}
                  required={required?.street}
                  {...stdFieldProps}
                />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
                <MuiRhfTextField
                  name={"postalCode"}
                  label={t("Company.postalCode")}
                  fullWidth={true}
                  required={required?.postalCode}
                  {...stdFieldProps}
                />
              </Grid>
              <Grid item={true} xs={8} lg={3}>
                <MuiRhfDatePicker name={'validityInfo.validFrom'} label={t('ValidityInfo.validFrom')}
                                  required={required?.validityInfo.validFrom}
                                  {...stdFieldProps} />
              </Grid>
              <Grid item={true} xs={8} lg={3}>
                <MuiRhfDatePicker name={'validityInfo.validTo'} label={t('ValidityInfo.validTo')}
                                  required={required?.validityInfo.validTo}
                                  {...stdFieldProps} />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
                <MuiRhfSelectField
                  name={"systemOfMeasurement"}
                  label={t("Company.systemOfMeasurement")}
                  fullWidth={true}
                  list={SYSTEM_OF_MEASUREMENTS}
                  getListLabel={(som) => t("SystemOfMeasurement." + som)}
                  required={required?.systemOfMeasurement}
                  {...stdListFieldProps}
                />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
                <MuiRhfSelectField
                  name={"currency"}
                  label={t("Company.currency")}
                  fullWidth={true}
                  list={CURRENCIES}
                  getListLabel={(currency) => t("Currency." + currency)}
                  required={required?.currency}
                  {...stdListFieldProps}
                />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
                <UploadButton
                  name={"logoFileId"}
                  mediaType="image/png"
                  required={required?.logoFileId}
                  disabled={formReadonly}
                  handleFileUpload={(file: File) =>
                    uploadFile(axios, file).then((uploadedFile) => {
                      setValue("logoFileId", uploadedFile.id);
                      uploadedFile.publicUUID && setValue("logoUrl", "/api/public/files/" + uploadedFile.publicUUID);
                    })
                  }
                />
                <CompanyImageField formReadonly={formReadonly} />
              </Grid>
              <Grid item={true} xs={12}>
                <ContactPersonForm disabled={formReadonly} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </RhfForm>
    </FormProvider>
  );
}

